import React, { useEffect, useState } from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { useNavigate } from 'react-router-dom';
import Icons from '../../../../components/Common/Icons/Icons';
import PageSubHeader from '../../Common/PageSubHeader';
import { Button } from '../../../../components/Common/UI/Button';
import accountManagementData from './Data';
import Separator from '../../../../components/Common/UI/Separator';
import AccountManagementFilters from './Filters';
import CustomTable from './CustomTable';
import accountManagementCol, { AccountManagementRes } from './columns';
import { useAccountManagementProvider } from '../../../context/accountManagement';

const data = accountManagementData;

export enum AccountTypeEnum {
  'accountNumber' = 'Account Number',
  'accountType' = 'Account Type',
  'accountName' = 'Account Name',
}

export const accountType: { key: keyof typeof AccountTypeEnum; label: string }[] = [
  { key: 'accountNumber', label: AccountTypeEnum.accountNumber },
  { key: 'accountType', label: AccountTypeEnum.accountType },
  { key: 'accountName', label: AccountTypeEnum.accountName },
];

function AccountManagement() {
  const navigate = useNavigate();
  const [exactSearch, setExactSearch] = useState<CheckedState>(false);
  const [search, setSearch] = useState<string>('8080501');
  const [selectedType, setSelectedType] = useState<keyof typeof AccountTypeEnum>('accountNumber');
  const { showTable, handleShowTable } = useAccountManagementProvider();

  const [tableData, setTableData] = useState<AccountManagementRes[]>([]);

  const handleOnSelect = (payload: keyof typeof AccountTypeEnum) => {
    setSelectedType(payload);
  };

  useEffect(() => {
    if (showTable) {
      setTableData(data);
    }
  }, [showTable]);

  const handleOnChange = (payload: string) => {
    setSearch(payload);
  };

  const handleCheckBox = (payload: CheckedState) => {
    setExactSearch(payload);
  };

  const handleSearch = () => {
    handleShowTable(true);

    if (!search) {
      setTableData(data);
    }

    if (selectedType && search) {
      if (exactSearch) {
        setTableData(data.filter(el => el[selectedType] === search));
      } else {
        setTableData(data.filter(el => el[selectedType].includes(search)));
      }

      return;
    }

    setTableData(data);
  };

  return (
    <div className="mb-[50px]">
      <PageSubHeader
        leftContent={
          <div className="flex items-center gap-2">
            <Icons.UserAccount className="size-6" />
            <span>Account Management</span>
          </div>
        }
        breadcrumbs={[
          { path: '/infoTrac/', label: <Icons.HomeOutlined />, disableBg: true },
          { path: '/infoTrac', label: 'InfoTRAC' },
          { path: '/infoTrac/account-management', label: 'Account Management' },
        ]}
      />

      <div className="page-heading px-4 pt-2 bg-slate-100">
        <div className="flex items-center justify-between">
          <div className="text-primary flex items-center gap-[10px] font-bold text-[20px]">
            Account Search
            <Icons.InfoFill />
          </div>
          <div className="flex items-center gap-4">
            <Button
              className="flex items-center gap-[10px]"
              variant="secondary"
              onClick={() => navigate('/infoTrac/account-management/create-account')}
            >
              <Icons.UserAccount />
              New Account
            </Button>
            <Button
              className="flex items-center gap-[10px] bg-primary"
              onClick={() => navigate('/infoTrac/account-management/create-user')}
            >
              <Icons.UserManagement className="w-[20px] h-[20px]" />
              New User
            </Button>
          </div>
        </div>
        <Separator className="mt-[10px]" />
        <AccountManagementFilters
          handleOnChange={handleOnChange}
          handleCheckBox={handleCheckBox}
          handleOnSelect={handleOnSelect}
          handleSearch={handleSearch}
          search={search}
          selectedType={selectedType}
        />
        <div className="text-primary pb-[10px] font-bold">Search Result:</div>
      </div>
      <CustomTable data={tableData} columns={accountManagementCol} />
    </div>
  );
}

export default AccountManagement;
